import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { fields, generateTimeSlots, updateFieldById } from '../../data/mockData';
import { getOwnerProfileById, getSession } from '../../data/authStore';
import {
  approveBookingByOwner,
  approveBookingPaymentByOwner,
  cancelBookingWithResult,
  getAllBookings,
  isSlotBlockedByOwner,
  rejectBookingPaymentByOwner,
  toggleOwnerSlotBlock,
} from '../../data/bookingStore';
import { approveSubscriptionByOwner, cancelSubscriptionByOwner, getFieldSubscriptionsForOwner } from '../../data/subscriptionStore';
import { dispatchBookingStatusMessages } from '../../services/messagingApi';
import { createBookingStatusNotifications } from '../../data/engagementStore';

const MONTH_LABELS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

function formatDateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(date: Date, offset: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + offset, 1);
}

function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function computeSlotCountForBookingDuration(duration: number): number {
  return Math.max(1, Math.round(duration));
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [refreshSeed, setRefreshSeed] = useState(0);
  void refreshSeed;
  const session = getSession();
  const ownerProfile = session?.role === 'owner' ? getOwnerProfileById(session.ownerId) : undefined;
  const ownerFields = fields.filter((f) => (session?.role === 'owner' ? f.ownerId === session.ownerId : false));
  const canViewDashboard =
    session?.role === 'owner' &&
    ownerProfile?.status === 'approved' &&
    ownerFields.length > 0;

  const [activeTab, setActiveTab] = useState<'overview' | 'bookings' | 'calendar'>('overview');
  const [selectedFieldId, setSelectedFieldId] = useState(ownerFields[0]?.id || '');
  const [calendarDate, setCalendarDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const ownerBookings = getAllBookings().filter(b => ownerFields.some(f => f.id === b.fieldId));
  const activeBookings = useMemo(
    () => ownerBookings.filter((b) => b.status !== 'cancelled'),
    [ownerBookings]
  );
  const ownerSubscriptions = ownerFields.flatMap((f) =>
    getFieldSubscriptionsForOwner(f.id).map((sub) => ({ ...sub, fieldName: f.name }))
  );

  const today = useMemo(() => new Date(), []);
  const currentMonthKey = formatDateKey(today);
  const prevMonthKey = formatDateKey(shiftMonth(today, -1));

  const monthlyBookings = useMemo(
    () => activeBookings.filter((b) => b.date.startsWith(currentMonthKey)),
    [activeBookings, currentMonthKey]
  );
  const prevMonthlyBookings = useMemo(
    () => activeBookings.filter((b) => b.date.startsWith(prevMonthKey)),
    [activeBookings, prevMonthKey]
  );

  const totalRevenue = useMemo(
    () => activeBookings.reduce((sum, b) => sum + b.totalPrice, 0),
    [activeBookings]
  );
  const monthlyRevenue = useMemo(
    () => monthlyBookings.reduce((sum, b) => sum + b.totalPrice, 0),
    [monthlyBookings]
  );
  const prevMonthlyRevenue = useMemo(
    () => prevMonthlyBookings.reduce((sum, b) => sum + b.totalPrice, 0),
    [prevMonthlyBookings]
  );

  const totalReviewCount = useMemo(
    () => ownerFields.reduce((sum, f) => sum + (f.reviewCount || 0), 0),
    [ownerFields]
  );
  const averageRating = useMemo(() => {
    if (totalReviewCount <= 0) return 0;
    const weighted = ownerFields.reduce((sum, f) => sum + (f.rating || 0) * (f.reviewCount || 0), 0);
    return Number((weighted / totalReviewCount).toFixed(1));
  }, [ownerFields, totalReviewCount]);

  const occupancyRate = useMemo(() => {
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    let totalSlots = 0;
    for (const field of ownerFields) {
      for (let day = 1; day <= monthEnd.getDate(); day += 1) {
        const date = toIsoDate(new Date(monthStart.getFullYear(), monthStart.getMonth(), day));
        totalSlots += generateTimeSlots(field.id, date).length;
      }
    }
    const bookedSlots = monthlyBookings.reduce((sum, b) => sum + computeSlotCountForBookingDuration(b.duration), 0);
    if (totalSlots <= 0) return 0;
    return Math.min(100, Math.round((bookedSlots / totalSlots) * 100));
  }, [monthlyBookings, ownerFields, today]);

  const monthlyRevenueChange = useMemo(() => {
    if (prevMonthlyRevenue <= 0) return null;
    return Math.round(((monthlyRevenue - prevMonthlyRevenue) / prevMonthlyRevenue) * 100);
  }, [monthlyRevenue, prevMonthlyRevenue]);

  const monthlyBookingsChange = useMemo(() => {
    if (prevMonthlyBookings.length <= 0) return null;
    return Math.round(((monthlyBookings.length - prevMonthlyBookings.length) / prevMonthlyBookings.length) * 100);
  }, [monthlyBookings.length, prevMonthlyBookings.length]);

  const upcomingBookings = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    return ownerBookings
      .filter(b => b.date >= today && b.status !== 'cancelled')
      .sort((a, b) => a.date.localeCompare(b.date) || a.hour - b.hour);
  }, [ownerBookings]);

  const calendarSlots = useMemo(() => {
    return generateTimeSlots(selectedFieldId, calendarDate);
  }, [selectedFieldId, calendarDate]);

  const weekDates = useMemo(() => {
    const dates: { date: string; label: string; dayName: string }[] = [];
    const dayNames = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      dates.push({
        date: dateStr,
        label: `${d.getDate()} ${['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'][d.getMonth()]}`,
        dayName: dayNames[d.getDay()],
      });
    }
    return dates;
  }, []);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };
  const formatHourValue = (value: number) => {
    const totalMinutes = Math.round(value * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  const getBookingStatusLabel = (status: string) => {
    if (status === 'confirmed') return 'Onaylı';
    if (status === 'pending_owner_approval' || status === 'pending') return 'SS Onayı Bekliyor';
    if (status === 'awaiting_payment') return 'Ödeme Bekliyor';
    if (status === 'payment_review') return 'Dekont İncelemede';
    return 'İptal';
  };

  const [ownerMessage, setOwnerMessage] = useState('');
  const [ownerActionInfo, setOwnerActionInfo] = useState('');

  const revenueData = useMemo(() => {
    return Array.from({ length: 6 }, (_, idx) => {
      const monthDate = shiftMonth(new Date(today.getFullYear(), today.getMonth(), 1), idx - 5);
      const key = formatDateKey(monthDate);
      const amount = activeBookings
        .filter((b) => b.date.startsWith(key))
        .reduce((sum, b) => sum + b.totalPrice, 0);
      return { month: MONTH_LABELS[monthDate.getMonth()], amount };
    });
  }, [activeBookings, today]);
  const maxRevenue = Math.max(...revenueData.map(r => r.amount), 1);

  // Popular hours analysis
  const popularHours = useMemo(() => {
    const hourCounts: Record<number, number> = {};
    ownerBookings.forEach(b => {
      if (b.status !== 'cancelled') {
        hourCounts[b.hour] = (hourCounts[b.hour] || 0) + 1;
      }
    });
    return Object.entries(hourCounts)
      .map(([hour, count]) => ({ hour: Number(hour), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [ownerBookings]);
  const maxPopularCount = Math.max(...popularHours.map(h => h.count), 1);

  const weeklyOccupancy = useMemo(() => {
    const dayLabels = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
    return Array.from({ length: 7 }, (_, idx) => {
      const d = new Date();
      d.setDate(d.getDate() + idx);
      const date = toIsoDate(d);
      const totalSlots = ownerFields.reduce((sum, field) => sum + generateTimeSlots(field.id, date).length, 0);
      const bookedSlots = activeBookings
        .filter((b) => b.date === date)
        .reduce((sum, b) => sum + computeSlotCountForBookingDuration(b.duration), 0);
      const rate = totalSlots > 0 ? Math.min(100, Math.round((bookedSlots / totalSlots) * 100)) : 0;
      return { day: dayLabels[d.getDay()], rate };
    });
  }, [activeBookings, ownerFields]);

  // Revenue by field
  const revenueByField = useMemo(() => {
    return ownerFields.map(field => {
      const fieldBookings = ownerBookings.filter(b => b.fieldId === field.id && b.status !== 'cancelled');
      const revenue = fieldBookings.reduce((sum, b) => sum + b.totalPrice, 0);
      return { name: field.name, revenue, bookings: fieldBookings.length };
    }).sort((a, b) => b.revenue - a.revenue);
  }, [ownerFields, ownerBookings]);
  const maxFieldRevenue = Math.max(...revenueByField.map(f => f.revenue), 1);

  const handleCancelBookingByOwner = async (bookingId: string) => {
    const result = cancelBookingWithResult(bookingId);
    if (!result.ok || !result.booking) {
      setOwnerActionInfo('Rezervasyon iptal edilemedi.');
      return;
    }
    const booking = result.booking;
    const owner = ownerProfile;
    createBookingStatusNotifications({
      status: 'cancelled',
      referenceCode: booking.id.toUpperCase(),
      fieldId: booking.fieldId,
      fieldName: booking.fieldName,
      date: booking.date,
      startTime: formatHourValue(booking.hour),
      endTime: formatHourValue(booking.hour + booking.duration),
      customerId: booking.customerId,
      customerPhone: booking.contactPhone,
      ownerId: session?.role === 'owner' ? session.ownerId : undefined,
      ownerPhone: owner?.phone,
    });
    try {
      await dispatchBookingStatusMessages({
        status: 'cancelled',
        referenceCode: booking.id.toUpperCase(),
        fieldName: booking.fieldName,
        date: booking.date,
        startTime: formatHourValue(booking.hour),
        endTime: formatHourValue(booking.hour + booking.duration),
        customerName: booking.contactName,
        customerPhone: booking.contactPhone,
        ownerName: owner?.fullName,
        ownerPhone: owner?.phone,
        byRole: 'owner',
        note: ownerMessage.trim() || undefined,
      });
      setOwnerActionInfo('Rezervasyon iptal edildi ve iki tarafa mesaj gönderildi.');
    } catch {
      setOwnerActionInfo('Rezervasyon iptal edildi, fakat mesaj servisine ulaşılamadı.');
    }
    setRefreshSeed((v) => v + 1);
  };

  const handleApproveBookingByOwner = (bookingId: string) => {
    if (!session || session.role !== 'owner') return;
    const result = approveBookingByOwner(bookingId, session.ownerId);
    if (!result.ok) {
      setOwnerActionInfo('Rezervasyon talebi onaylanamadı.');
      return;
    }
    setOwnerActionInfo('Talep onaylandı. Müşteri ödemeyi tamamladığında rezervasyon kesinleşecek.');
    setRefreshSeed((v) => v + 1);
  };

  const handleApprovePaymentByOwner = (bookingId: string) => {
    if (!session || session.role !== 'owner') return;
    const result = approveBookingPaymentByOwner(bookingId, session.ownerId);
    if (!result.ok) {
      setOwnerActionInfo(result.error || 'Ödeme onayı yapılamadı.');
      return;
    }
    setOwnerActionInfo('Dekont onaylandı. Rezervasyon kesinleşti.');
    setRefreshSeed((v) => v + 1);
  };

  const handleRejectPaymentByOwner = (bookingId: string) => {
    if (!session || session.role !== 'owner') return;
    const result = rejectBookingPaymentByOwner(bookingId, session.ownerId, ownerMessage.trim() || undefined);
    if (!result.ok) {
      setOwnerActionInfo(result.error || 'Ödeme reddedilemedi.');
      return;
    }
    setOwnerActionInfo('Dekont reddedildi. Müşteriden yeni ödeme/dekont bekleniyor.');
    setRefreshSeed((v) => v + 1);
  };

  const handleToggleSlotBlock = (fieldId: string, date: string, hour: number) => {
    const result = toggleOwnerSlotBlock(fieldId, date, hour);
    if (!result.ok) {
      setOwnerActionInfo('Bu saatte rezervasyon olduğu için kapatma işlemi yapılamadı.');
      return;
    }
    setOwnerActionInfo(result.blocked ? 'Saat rezervasyona kapatıldı.' : 'Saat tekrar rezervasyona açıldı.');
    setRefreshSeed((v) => v + 1);
  };

  if (!canViewDashboard) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white rounded-xl border border-gray-200 shadow-sm p-6 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Bu Panel Sadece Sahası Olan Kullanıcılara Açık</h2>
          <p className="text-sm text-gray-600 mb-5">
            Rezervasyon yapmak isteyen kullanıcılar sahaları ve saha profilini görebilir, buradan rezervasyonlarına devam edebilir.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => navigate('/sahalar')}
              className="rounded-lg border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm"
            >
              Sahaları Gör
            </button>
            <button
              onClick={() => navigate('/panel/saha-ekle')}
              className="rounded-lg bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm"
            >
              Saha Ekle
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Saha Sahibi Paneli</h1>
          <p className="text-gray-500 mt-1">Hoş geldiniz! Sahalarınızı ve rezervasyonlarınızı buradan yönetebilirsiniz.</p>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 bg-white rounded-xl p-1 mb-8 max-w-lg shadow-sm border border-gray-200">
          {[
            { key: 'overview' as const, label: 'Genel Bakış' },
            { key: 'bookings' as const, label: 'Rezervasyonlar' },
            { key: 'calendar' as const, label: 'Takvim' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                activeTab === key
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => navigate('/panel/saha-ekle')}
            className="flex items-center justify-center gap-1.5 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors text-gray-600 hover:text-gray-900"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Saha Ekle
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{monthlyRevenue.toLocaleString('tr-TR')}₺</p>
                <p className="text-gray-500 text-sm">Aylık Gelir</p>
                <p className={`text-xs mt-1 ${monthlyRevenueChange === null ? 'text-gray-500' : monthlyRevenueChange >= 0 ? 'text-green-600' : 'text-rose-600'}`}>
                  {monthlyRevenueChange === null ? 'Geçen ay verisi yok' : `${monthlyRevenueChange > 0 ? '+' : ''}${monthlyRevenueChange}% geçen aya göre`}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{monthlyBookings.length}</p>
                <p className="text-gray-500 text-sm">Aylık Rezervasyon</p>
                <p className={`text-xs mt-1 ${monthlyBookingsChange === null ? 'text-gray-500' : monthlyBookingsChange >= 0 ? 'text-blue-600' : 'text-rose-600'}`}>
                  {monthlyBookingsChange === null ? 'Geçen ay verisi yok' : `${monthlyBookingsChange > 0 ? '+' : ''}${monthlyBookingsChange}% geçen aya göre`}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">%{occupancyRate}</p>
                <p className="text-gray-500 text-sm">Doluluk Oranı</p>
                <p className="text-yellow-600 text-xs mt-1">Bu ayki doluluk</p>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{averageRating}</p>
                <p className="text-gray-500 text-sm">Ortalama Puan</p>
                <p className="text-purple-600 text-xs mt-1">{totalReviewCount} toplam değerlendirme</p>
              </div>
            </div>

            {/* Revenue Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-6">Aylık Gelir</h3>
              <div className="flex items-end space-x-4 h-48">
                {revenueData.map((item) => (
                  <div key={item.month} className="flex-1 flex flex-col items-center">
                    <span className="text-gray-600 text-xs mb-2">{(item.amount / 1000).toFixed(1)}K</span>
                    <div
                      className="w-full bg-gradient-to-t from-primary-500 to-primary-400 rounded-t-lg transition-all duration-500"
                      style={{ height: `${(item.amount / maxRevenue) * 100}%` }}
                    />
                    <span className="text-gray-500 text-xs mt-2">{item.month}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Popular Hours & Weekly Occupancy */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Popular Hours */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-gray-900 font-semibold text-lg mb-6">En Popüler Saatler</h3>
                <div className="space-y-3">
                  {popularHours.length > 0 ? popularHours.map(item => (
                    <div key={item.hour} className="flex items-center gap-3">
                      <span className="text-sm text-gray-600 font-mono w-14">{formatHourValue(item.hour)}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary-400 to-primary-600 rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                          style={{ width: `${(item.count / maxPopularCount) * 100}%`, minWidth: '30px' }}
                        >
                          <span className="text-white text-xs font-semibold">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-400 text-sm">Henüz yeterli veri yok.</p>
                  )}
                </div>
              </div>

              {/* Weekly Occupancy */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-gray-900 font-semibold text-lg mb-6">Haftalık Doluluk Oranı</h3>
                <div className="flex items-end space-x-3 h-48">
                  {weeklyOccupancy.map(item => (
                    <div key={item.day} className="flex-1 flex flex-col items-center">
                      <span className="text-gray-600 text-xs mb-2 font-semibold">%{item.rate}</span>
                      <div className="w-full relative rounded-t-lg overflow-hidden" style={{ height: `${item.rate * 1.6}px` }}>
                        <div className={`absolute inset-0 rounded-t-lg ${
                          item.rate >= 80 ? 'bg-gradient-to-t from-red-500 to-red-400' :
                          item.rate >= 60 ? 'bg-gradient-to-t from-amber-500 to-amber-400' :
                          'bg-gradient-to-t from-primary-500 to-primary-400'
                        }`} />
                      </div>
                      <span className="text-gray-500 text-xs mt-2 font-medium">{item.day}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-primary-500" /><span className="text-gray-500">Düşük</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-500" /><span className="text-gray-500">Orta</span></div>
                  <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500" /><span className="text-gray-500">Yüksek</span></div>
                </div>
              </div>
            </div>

            {/* Revenue by Field */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-6">Saha Bazlı Gelir</h3>
              <div className="space-y-4">
                {revenueByField.map(item => (
                  <div key={item.name}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700 font-medium truncate max-w-[200px]">{item.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-400">{item.bookings} rez.</span>
                        <span className="text-sm font-semibold text-primary-600">{item.revenue.toLocaleString('tr-TR')}₺</span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className="h-full bg-gradient-to-r from-primary-400 to-emerald-500 rounded-full transition-all duration-700"
                        style={{ width: `${(item.revenue / maxFieldRevenue) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* My Fields */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Sahalarım</h3>
              <div className="space-y-3">
                {ownerFields.map((field) => (
                  <div
                    key={field.id}
                    className="flex items-center justify-between bg-white rounded-lg p-4 border border-gray-100 hover:border-gray-200 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <img src={field.images[0]} alt={field.name} className="w-12 h-12 rounded-lg object-cover" />
                      <div>
                        <h4 className="text-gray-900 font-medium">{field.name}</h4>
                        <p className="text-gray-500 text-sm">{field.district} &bull; {(field.sizeOptions || [field.size]).join(', ')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-primary-600 font-semibold">{field.pricePerHour}₺/saat</p>
                      <p className={`text-xs mt-1 ${field.isReservationPaused ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {field.isReservationPaused ? 'Rezervasyon Kapalı' : 'Rezervasyon Açık'}
                      </p>
                      <div className="flex items-center text-sm">
                        <svg className="w-3.5 h-3.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        <span className="text-gray-500 ml-1">{field.rating}</span>
                      </div>
                      <div className="mt-3 flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => navigate(`/panel/saha-ekle?edit=${field.id}`)}
                          className="text-xs rounded-lg border border-gray-300 bg-white text-gray-700 px-2.5 py-1"
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            updateFieldById(field.id, {
                              isReservationPaused: !field.isReservationPaused,
                              reservationPausedMessage: !field.isReservationPaused
                                ? 'Şuanlık rezervasyon alımı durmuştur.'
                                : '',
                            });
                            setRefreshSeed((v) => v + 1);
                          }}
                          className={`text-xs rounded-lg px-2.5 py-1 ${
                            field.isReservationPaused
                              ? 'bg-emerald-600 text-white'
                              : 'bg-rose-600 text-white'
                          }`}
                        >
                          {field.isReservationPaused ? 'Rezervasyonu Aç' : 'Rezervasyonu Durdur'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Stats Summary */}
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Toplam İstatistikler</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-gray-500 text-sm">Toplam Gelir</p>
                  <p className="text-gray-900 text-xl font-bold">{totalRevenue.toLocaleString('tr-TR')}₺</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Toplam Rezervasyon</p>
                  <p className="text-gray-900 text-xl font-bold">{activeBookings.length}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Aktif Saha</p>
                  <p className="text-gray-900 text-xl font-bold">{ownerFields.length}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Ortalama Gelir/Saha</p>
                  <p className="text-gray-900 text-xl font-bold">
                    {ownerFields.length > 0 ? Math.round(totalRevenue / ownerFields.length).toLocaleString('tr-TR') : '0'}₺
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Saha Sahibine Not / Mesaj Şablonu</label>
              <textarea
                rows={2}
                value={ownerMessage}
                onChange={(e) => setOwnerMessage(e.target.value)}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="İptal/değişiklik bildirimlerine eklenecek not"
              />
              {ownerActionInfo && <p className="text-xs text-indigo-700 mt-2">{ownerActionInfo}</p>}
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Yaklaşan Rezervasyonlar</h3>

              {upcomingBookings.length > 0 ? (
                <div className="space-y-3">
                  {upcomingBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-2 h-12 rounded-full ${
                          booking.status === 'confirmed'
                            ? 'bg-green-500'
                            : booking.status === 'payment_review'
                            ? 'bg-indigo-500'
                            : booking.status === 'awaiting_payment'
                            ? 'bg-blue-500'
                            : 'bg-yellow-500'
                        }`} />
                        <div>
                          <h4 className="text-gray-900 font-medium">{booking.teamName}</h4>
                          <p className="text-gray-500 text-sm">{booking.fieldName}</p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <p className="text-gray-400 text-xs">Tarih</p>
                          <p className="text-gray-900 text-sm font-medium">{formatDate(booking.date)}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-400 text-xs">Saat</p>
                          <p className="text-gray-900 text-sm font-medium">
                            {formatHourValue(booking.hour)} - {formatHourValue(booking.hour + booking.duration)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-400 text-xs">İletişim</p>
                          <p className="text-gray-900 text-sm font-medium">{booking.contactName}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-gray-400 text-xs">Tutar</p>
                          <p className="text-primary-600 text-sm font-bold">{booking.totalPrice}₺</p>
                        </div>
                        <div>
                          <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : booking.status === 'payment_review'
                              ? 'bg-indigo-100 text-indigo-700'
                              : booking.status === 'awaiting_payment'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {getBookingStatusLabel(booking.status)}
                          </span>
                        </div>
                        {booking.status === 'payment_review' && (
                          <div className="text-left max-w-xs">
                            {booking.paymentProofUrl && (
                              <a
                                href={booking.paymentProofUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-indigo-700 underline"
                              >
                                Dekontu Aç
                              </a>
                            )}
                            {booking.paymentProofNote && (
                              <p className="text-xs text-gray-600 mt-1">{booking.paymentProofNote}</p>
                            )}
                          </div>
                        )}
                        {(booking.status === 'pending_owner_approval' || booking.status === 'pending') && (
                          <button
                            type="button"
                            onClick={() => handleApproveBookingByOwner(booking.id)}
                            className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1"
                          >
                            Talebi Onayla
                          </button>
                        )}
                        {booking.status === 'payment_review' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleApprovePaymentByOwner(booking.id)}
                              className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1"
                            >
                              Ödemeyi Onayla
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRejectPaymentByOwner(booking.id)}
                              className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-700 px-2.5 py-1"
                            >
                              Dekontu Reddet
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => handleCancelBookingByOwner(booking.id)}
                          className="text-xs rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-2.5 py-1"
                        >
                          İptal Et
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">Yaklaşan rezervasyon bulunmamaktadır.</p>
                </div>
              )}
            </div>

            {/* All Bookings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Tüm Rezervasyonlar</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left text-gray-500 font-medium py-3 px-2">Takım</th>
                      <th className="text-left text-gray-500 font-medium py-3 px-2">Saha</th>
                      <th className="text-left text-gray-500 font-medium py-3 px-2">Tarih</th>
                      <th className="text-left text-gray-500 font-medium py-3 px-2">Saat</th>
                      <th className="text-left text-gray-500 font-medium py-3 px-2">Tutar</th>
                      <th className="text-left text-gray-500 font-medium py-3 px-2">Durum</th>
                      <th className="text-left text-gray-500 font-medium py-3 px-2">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ownerBookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-2 text-gray-900">{booking.teamName}</td>
                        <td className="py-3 px-2 text-gray-500">{booking.fieldName}</td>
                        <td className="py-3 px-2 text-gray-500">{formatDate(booking.date)}</td>
                        <td className="py-3 px-2 text-gray-500">
                          {formatHourValue(booking.hour)}
                        </td>
                        <td className="py-3 px-2 text-primary-600 font-medium">{booking.totalPrice}₺</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            booking.status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : booking.status === 'payment_review'
                              ? 'bg-indigo-100 text-indigo-700'
                              : booking.status === 'awaiting_payment'
                              ? 'bg-blue-100 text-blue-700'
                              : booking.status === 'pending' || booking.status === 'pending_owner_approval'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {getBookingStatusLabel(booking.status)}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-2">
                            {(booking.status === 'pending_owner_approval' || booking.status === 'pending') && (
                              <button
                                type="button"
                                onClick={() => handleApproveBookingByOwner(booking.id)}
                                className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1"
                              >
                                Onayla
                              </button>
                            )}
                            {booking.status === 'payment_review' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleApprovePaymentByOwner(booking.id)}
                                  className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1"
                                >
                                  Ödemeyi Onayla
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRejectPaymentByOwner(booking.id)}
                                  className="text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-700 px-2.5 py-1"
                                >
                                  Dekontu Reddet
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              disabled={booking.status === 'cancelled'}
                              onClick={() => handleCancelBookingByOwner(booking.id)}
                              className="text-xs rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-2.5 py-1 disabled:opacity-50"
                            >
                              İptal Et
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Aboneler ve Tarihleri</h3>
              {ownerSubscriptions.length === 0 ? (
                <p className="text-sm text-gray-500">Aktif abone bulunmuyor.</p>
              ) : (
                <div className="space-y-3">
                  {ownerSubscriptions.map((sub) => (
                    <div key={sub.id} className="rounded-lg border border-gray-200 p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{sub.fieldName}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Gün: {sub.dayKey.toUpperCase()} • Saat: {sub.timeLabel} • Başlangıç: {sub.startDate} •
                          {' '}
                          {sub.status === 'pending_owner_approval'
                            ? 'Onay Bekliyor'
                            : sub.status === 'awaiting_payment'
                            ? 'Ödeme Bekliyor'
                            : 'Aktif'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {sub.status === 'pending_owner_approval' && (
                          <button
                            type="button"
                            onClick={() => {
                              const result = approveSubscriptionByOwner(sub.id, sub.fieldId);
                              if (result.ok) {
                                setOwnerActionInfo('Abonelik talebi onaylandı, müşteriden ödeme bekleniyor.');
                                setRefreshSeed((v) => v + 1);
                              }
                            }}
                            className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1"
                          >
                            Onayla
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const result = cancelSubscriptionByOwner(sub.id, sub.fieldId);
                            if (result.ok) {
                              setOwnerActionInfo(`Abonelik iptal edildi, ${result.refundAmount}₺ iade edildi.`);
                              setRefreshSeed((v) => v + 1);
                            }
                          }}
                          className="text-xs rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-2.5 py-1"
                        >
                          Aboneliği İptal Et
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            {/* Field Selector */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Saha Seçin</h3>
              <div className="flex flex-wrap gap-3">
                {ownerFields.map((field) => (
                  <button
                    key={field.id}
                    onClick={() => setSelectedFieldId(field.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedFieldId === field.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:border-primary-400'
                    }`}
                  >
                    {field.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Calendar View */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-gray-900 font-semibold text-lg mb-4">Haftalık Takvim</h3>

              {/* Date Selector */}
              <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
                {weekDates.map(({ date, label, dayName }) => (
                  <button
                    key={date}
                    onClick={() => setCalendarDate(date)}
                    className={`flex-shrink-0 px-4 py-3 rounded-xl text-center transition-colors min-w-[80px] ${
                      calendarDate === date
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                    }`}
                  >
                    <div className="text-xs font-medium">{dayName}</div>
                    <div className="text-sm font-semibold mt-1">{label}</div>
                  </button>
                ))}
              </div>

              {/* Time Slots */}
              <div className="space-y-2">
                {calendarSlots.map((slot) => {
                  const booking = ownerBookings.find(
                    b => b.status !== 'cancelled' && b.fieldId === selectedFieldId && b.date === calendarDate && b.hour <= slot.hour && b.hour + b.duration > slot.hour
                  );
                  const ownerBlocked = isSlotBlockedByOwner(selectedFieldId, calendarDate, slot.hour);
                  return (
                    <div
                      key={slot.hour}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        slot.isBooked
                          ? 'bg-red-50 border border-red-200'
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <span className="text-gray-900 font-mono w-16">
                          {slot.label || formatHourValue(slot.hour)}
                        </span>
                        <div className={`w-2 h-2 rounded-full ${slot.isBooked ? 'bg-red-400' : 'bg-green-400'}`} />
                        <span className={slot.isBooked ? 'text-red-600' : 'text-gray-500'}>
                          {slot.isBooked ? (booking ? booking.teamName : ownerBlocked ? 'SS Kapattı' : 'Rezerve') : 'Müsait'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        {booking && (
                          <>
                          <span className="text-gray-500">{booking.contactName}</span>
                          <span className="text-primary-600 font-medium">{booking.totalPrice / booking.duration}₺</span>
                          </>
                        )}
                        {!booking && (
                          <button
                            type="button"
                            onClick={() => handleToggleSlotBlock(selectedFieldId, calendarDate, slot.hour)}
                            className={`text-xs rounded-lg px-2.5 py-1 ${
                              ownerBlocked
                                ? 'bg-emerald-600 text-white'
                                : 'bg-rose-600 text-white'
                            }`}
                          >
                            {ownerBlocked ? 'Saati Aç' : 'Saati Kapat'}
                          </button>
                        )}
                        {booking && (
                          <button
                            type="button"
                            onClick={() => handleCancelBookingByOwner(booking.id)}
                            className="text-xs rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-2.5 py-1"
                          >
                            İptal Et
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center space-x-6 mt-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="text-gray-500">Müsait</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <span className="text-gray-500">Dolu</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
